import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { buildAbSDKRouteState, isAbSDKRouteState } from "abweb/service/router/absdkRouter/AbSDKRouteState";
import { BrowserRouteChangeContext } from "abweb/service/router/absdkRouter/BrowserRouteChangeContext";
import { BrowserRoute } from "abweb/service/router/BrowserRoute";
import { ContinueRouteChangeCallbacks } from "abweb/service/router/ContinueRouteChange";
import { calcRouteWithParameters } from "abweb/service/router/RouteEmitter";
import { buildParamMap, matchRoute, routeTemplateToRegex } from "abweb/service/router/RouteParser";
import { DebugUtils } from "abweb/utils/DebugUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ManualPromise } from "abweb/utils/ManualPromise";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
import { URLUtils } from "abweb/utils/URLUtils";

// Can not use stronger RouteCallback type or break backward compatibility with IRouter (and KendoRouter)

// Actual type RouteCallbackNo = (...params: (string | undefined)[], extraParams: Record<string, string | undefined>) => void;   // callback(...params: string|undefined, extraParams: Record<string, string | undefined>)

const LOG = LogUtils.getLogger(__filename);
export class AbSDKRouter {
  constructor() {
    _defineProperty(this, "routeConfigs", []);
    _defineProperty(this, "continueCallbacks", new ContinueRouteChangeCallbacks());
    _defineProperty(this, "isDestroyed", false);
    // currentRouteState should equal window.history.state AFTER first bookmark/replace
    _defineProperty(this, "currentRouteState", AbSDKRouter.UNINITIALIZED_ROUTE_STATE);
    _defineProperty(this, "isInRevertFn", false);
    _defineProperty(this, "_isRunning", false);
    _defineProperty(this, "buildUnknownRouteUrlFn", void 0);
    _defineProperty(this, "onPopStateListener", ev => {
      // PopState (back/forward) has ALREADY manipulated history stack by the time it gets here,
      // so do NOT do bookmark or replace (browserInitiated=true)
      if (this.isInRevertFn) {
        this.onRevertStateListener(ev);
        return;
      }
      LOG.debug(`**onPopStateListener START this.currentRouteState: route=${this.currentRouteState.__routeState.route}, timestamp=${this.currentRouteState.__routeState.timestamp}`);
      const newRoute = this.getRouteFromUrl();
      if (newRoute !== this.currentRouteState.__routeState.route) {
        const newRouteState = ev.state ?? buildAbSDKRouteState(newRoute);
        const browserRouteChgContext = this.createBrowserRouteChangeContext(this.currentRouteState, newRouteState);
        this.displayRoute(newRoute, browserRouteChgContext);
        LOG.debug(`**onPopStateListener PROCESSED newRouteState: route=${newRouteState.__routeState.route}, timestamp=${newRouteState.__routeState.timestamp}`);
      } else {
        // Typically only hit by Back/Forward btn workflows that were vetoed (and their revertToPreviousRoute calls
        // to window.history.{back|forward}()).  Don't want to kick off a subsequent displayRoute
        // when the current route is already the 'correct' route.
        LOG.info(`Ignored route change event to following route as already the current route: '${newRoute}'`);
        // If url-navigate to same route as current url, browser still generates a popState event and loses current window.history.state
        // Restore the window.history.state.  See ABSDK-4880
        if (window.history.state !== this.currentRouteState) {
          LOG.debug("Force restore of window.history.state to match currentRouteState");
          this.replaceWithRouteState(this.currentRouteState, true);
        }
      }
      LOG.debug(`**onPopStateListener END this.currentRouteState: route=${this.currentRouteState.__routeState.route}, timestamp=${this.currentRouteState.__routeState.timestamp}`);
    });
    _defineProperty(this, "revertBackFn", () => {
      LOG.debug(`**revertBackFn() invoked`);
      this.isInRevertFn = true;
      window.history.back();
      // More elegant 'wrapper' below fails as window.history.back() returns before onPopStateListener invoked
      // try {
      //   this.isInRevertFn = true;
      //   window.history.back();
      // }
      // finally {
      //   this.isInRevertFn = false;
      // }
    });
    _defineProperty(this, "revertForwardFn", () => {
      LOG.debug(`**revertForwardFn() invoked`);
      this.isInRevertFn = true;
      window.history.forward();
    });
    this.buildUnknownRouteUrlFn = this.addDefaultUnknownRoute();
  }

  /**
   * Register a new route
   * @param routeTemplate - Route pattern with optional named parameters (e.g., '/users/:id')
   * @param callback - Function to execute when route matches
   */
  addRoute(routeTemplate, callback) {
    // Extract route regEx and parameter names from the route pattern
    const {
      routeRegEx: pattern,
      paramNames
    } = routeTemplateToRegex(routeTemplate);
    // Small optimization: put '/' as first elem in routeConfigs so that it is found quickly in O(n) scan
    if (routeTemplate === "/") {
      this.routeConfigs.unshift({
        routeTemplate,
        routeRegEx: pattern,
        routeCallback: callback,
        paramNames
      });
    } else {
      this.routeConfigs.push({
        routeTemplate,
        routeRegEx: pattern,
        routeCallback: callback,
        paramNames
      });
    }
  }

  /** @inheritDoc */
  removeRoute(routeTemplate) {
    const foundIndex = this.routeConfigs.findIndex(routeConfig => routeConfig.routeTemplate === routeTemplate);
    // Using 'delete' operator leaves hole in array (length not changed), so use splice.
    if (foundIndex !== -1) {
      this.routeConfigs.splice(foundIndex, 1);
    }
  }

  /** @inheritDoc */
  start() {
    this.destroyGuard();
    if (this._isRunning) {
      throw new Error("AbSDKRouter is already running yet start() was invoked.");
    }
    this._isRunning = true;

    // Required that there is a default route registered, as have revertToHomeFn below'
    // that defaults to '/' when navigating from another route.  Fail fast instead of subtle errors later...
    const matchedRoute = this.getMatchedRoute("/");
    if (!matchedRoute) {
      // Example: Trivial default route registration for testing only:
      // AbWebServices.router.addRoute("/", () => {
      //   LOG.info("Default home route '/' rendered!");
      // });
      throw new Error("The AbSDKRouter requires a default route '/' to be registered before starting the router.");
    }

    // Listen for popstate events (browser back/forward)
    // eslint-disable-next-line abinitio/check-listeners
    window.addEventListener('popstate', this.onPopStateListener);

    // Handle current route on route start
    const newRoute = this.getRouteFromUrl();

    // When navigating from 'external' site to the app, if there is an INVALID route specified want to remain on the
    // app in the home page.  As such, override the revertFn on the initial url that starts the app to
    // display the app home page if the newRoute is invalid for the app (will display 'missing route' alert, then goto '/')
    const revertToHomeFn = () => window.location.hash = "/"; // causes popState event
    const newRouteState = buildAbSDKRouteState(newRoute);
    const browserRCC = this.createBrowserRouteChangeContext(undefined, newRouteState, revertToHomeFn);
    LOG.debug(`**start() initial url state: route=${newRouteState.__routeState.route}, timestamp=${newRouteState.__routeState.timestamp}`);
    this.displayRoute(newRoute, browserRCC);
  }

  /** @inheritDoc */
  get isRunning() {
    return this._isRunning;
  }
  onRevertStateListener(ev) {
    this.isInRevertFn = false; // Reset revertFn short-circuit.  Gross - see revertBackFn explanation.
    LOG.debug(`**onRevertStateListener START`);

    // Revert fn invoked (back()|forward()) as navigating to new route was vetoed.
    // currentRH was never unmounted, so just need to update url/routeState to the reverted route,
    // (doesn't matter if revertRoute is dirty - do NOT check!).  Do NOT have to (re)display the route
    // as it was never unmounted.
    if (!isAbSDKRouteState(ev.state)) {
      throw new Error(`Revert history event did not contain AbSDKRouteState: '${JSON.stringify(ev, undefined, 2)}'`); // defensive
    }
    const revertRouteState = ev.state;
    this.replaceWithRouteState(revertRouteState);
    LOG.debug(`**onRevertStateListener END reverted to RouteState: route=${revertRouteState.__routeState.route}, timestamp=${revertRouteState.__routeState.timestamp}`);
  }
  createBrowserRouteChangeContext(currentRouteState, newRouteState, revertFnOverride) {
    return new BrowserRouteChangeContext(this, currentRouteState, newRouteState, revertFnOverride);
  }

  /**
   * Get the current path from the URL
   */
  getRouteFromUrl() {
    const anchorRoute = window.location.hash;
    // Only url routes will default to '/' if not provided; other code paths expect non-empty route.
    return anchorRoute ? this.canonicalRoute(anchorRoute) // remove leading '#'
    : "/"; // default to home route
  }
  canonicalRoute(route) {
    // KendoRouter automatically stripped leading # from routes, so do the same for backward compatibility
    return route?.startsWith("#") ? route.substring(1) : route;
  }

  /////  Route Display ///////

  /** @inheritDoc */
  navigate(newRoute) {
    // add current route to history
    newRoute = this.canonicalRoute(newRoute);
    this.displayRoute(newRoute, undefined, true);
  }

  /**
   * Handle routing based on current path
   * @param newRoute (string) such as "/foo/bar"
   * @param browserRouteChgContext (BrowserRouteChangeContext) If navigating to newRoute via browser-initiated change,
   * provide the historyRouteState from the popstate event so that can determine if
   * it was initiated via the back button.  If code-initiated route change then browserRouteChgContext will be undefined.
   * @param bookmarkBeforeRender true if the current url/title should be put on Back History before navigating to newRoute.
   */
  displayRoute(newRoute, browserRouteChgContext, bookmarkBeforeRender = false) {
    // 1. Make sure if is valid route (throws route not found ex) that is not being vetoed
    // 1a. newRoute matches a registered route
    let matchedRoute = this.getMatchedRoute(newRoute);

    // 1b. No matching route, so goto the UnknownRoute instead to display the error to user
    // The UnknownRoute will still kick off the veto workflow for the current route.  See PORTAL-1010.
    if (!matchedRoute) {
      matchedRoute = this.getUnknownRoute(newRoute); // throws if not matched!
      LOG.error(`Route ${newRoute} is unknown, will go to UnknownRoute instead.`);
    }

    // 1c.  Make sure route change is not vetoed (e.g., typically as current route hasUnsavedChanges)
    // handleVetoedRouteChange handles the veto workflow (including revert and retry setup)
    const isVetoed = this.handleVetoWorkflow(matchedRoute, browserRouteChgContext);
    if (isVetoed) {
      return false;
    }

    // 2. Update currentRouteState via either bookmark or replaceWithRouteState
    if (bookmarkBeforeRender) {
      this.bookmark(newRoute); // only router.navigate(route) sets bookmarkBeforeRender=true
    } else {
      // We're going to render the route via the routeCallback, so set the
      // routeState (and this.currentRouteState) appropriately.
      const updateCurrentState = browserRouteChgContext?.newRouteState ?? buildAbSDKRouteState(newRoute);
      this.replaceWithRouteState(updateCurrentState);
    }
    LOG.debug(`**displayRoute this.currentRouteState: route=${this.currentRouteState.__routeState.route}, timestamp=${this.currentRouteState.__routeState.timestamp}`);

    // 3. Render the route by invoking the route's registered routeCallback with params
    //    to display the route.  Convention is extraParams is always the last arg.
    const {
      routeConfig,
      paramValues,
      extraParams
    } = matchedRoute;
    routeConfig.routeCallback(...paramValues, extraParams);
    return true;
  }

  /**
   * Determine if a route change needs to be cancelled, and if so handles the veto workflow.
   *
   * @returns true if the route change was vetoed,
   * and false to signify that the route change can continue (was not vetoed).
   */
  handleVetoWorkflow(matchedRoute, browserRouteChgContext) {
    const newRoute = matchedRoute.route; // might be 'unknown route'!
    // Determine if route change should be vetoed, which happens when current route is dirty.
    // The NavigationService continueRouteChange will return immediately with false, but it
    // also spawns a new async thread for the veto workflow that will retry the route change
    // after the user saves/discards current route changes.  Note that the continueUrlChangeHandler
    // does NOT start their save/discard workflow until after the browser history is reverted to the
    // correct state by this method, which is signaled by the afterReverP promise being resolved.
    //
    // Do NOT let back/forward history stack manipulation leak into routeCallback (should be solely handled here!)
    const afterRevertP = new ManualPromise();
    if (this.continueUrlChangeHandler(matchedRoute, browserRouteChgContext?.retryFn, afterRevertP)) {
      return false; // return false to signal route change was not vetoed
    }

    // Subtle: the continueUrlChange handler has launched the 'veto workflow' async thread,
    // that can retry the route change again after success (Save/Discard).  Abort this attempt.
    const initiatedTypePrefix = !!browserRouteChgContext ? "browser" : "code";
    LOG.info(`Canceling ${initiatedTypePrefix}-initiated route change to: '${newRoute}'`);
    if (browserRouteChgContext) {
      // browser initiated route changes have already changed the history stack, so revert it depending on how
      // route change was initiated (browser BACK/FORWARD buttons, or 'manual' browser url change)
      // Do NOT have to revert non-browser initiated route changes as url wasn't yet updated (stays on current route)
      const revertFnP = browserRouteChgContext.revertFn();
      // After browser history finished reverted, THEN kick off 'afterRevert' workflow (aka retry route change, aka saveDiscardCancel dialog)
      revertFnP.finally(() => afterRevertP.resolve());
    } else {
      // Code-initiated route changes have not changed history stack, so it is already in 'correct' state, so kick off 'afterRevert' workflow
      afterRevertP.resolve();
    }
    return true; // return true to signal route change was vetoed (and veto workflow was initiated)
  }
  getMatchedRoute(routeWithExtraParams) {
    // Concerned about getMatchedRoute performance when have hundreds (thousands?) of routes in Portal as O(N) regex
    // evaluations.
    // First step: put in profiling (ABSDK-?).   Based on results, might need to do some of the optimizations below:
    // Turns out typical getMatchedRoute invocation is around 1ms for abreact workbench (140 routes),
    // so no further optimization needed other than first two strategies mentioned below

    // 1. Done: keep '/' as first route as most likely to be requested
    // 2. Done: after match, insert route to beginning of parsedRouteTemplates as likely to be invoked again
    //    via browser back/forward buttons.  Essentially keeps parsedRouteTemplates in Most Recently Used order.
    // 3. router would proactively reorder based on mounting major newRoute (including goto-initiated new routes)
    // 4: build some sort of 'trie' based on route prefix.  Would only need to do expensive regex for
    //    routes that start with the given route prefix.  The route prefix is everything up to first route param.
    //    For routes that have optional params before required params, route may be registered under multiple trie paths
    //    Example route:
    //      (/workspace/:workspace)/myRoute/:param1
    //    could be registered under both /workspace prefix and /myRoute prefix.
    return ProfileUtils.elapsedSync("AbSDKRouter::getMatchedRoute", () => {
      // Use default route if no route provided
      routeWithExtraParams = routeWithExtraParams ? routeWithExtraParams : "/";
      const {
        route,
        extraParams
      } = this.parseExtraParams(routeWithExtraParams);
      for (const [index, routeConfig] of this.routeConfigs.entries()) {
        const params = matchRoute(routeConfig.routeRegEx, routeConfig.paramNames, route);
        if (params) {
          // Small optimization: Keep routeConfigs in Most Recently Used order by pushing match to top so get better O(n) matching
          if (index > 0) {
            const routeConfigTmp = this.routeConfigs[index];
            this.routeConfigs.splice(index, 1);
            this.routeConfigs.unshift(routeConfigTmp);
          }
          return {
            route: routeWithExtraParams,
            routeConfig,
            paramValues: params,
            extraParams
          };
        }
      }
      return undefined;
    });
  }

  /////  History Manipulation ///////

  /** @inheritDoc */
  bookmark(newRoute) {
    this.destroyGuard();
    newRoute = this.canonicalRoute(newRoute);

    // Check does not match current url.
    // Previously did not do this, and on 'popstate' events(e.g. Back button),
    // the new RH would call this but the history/current stack already reflected the
    // new url (and calling pushState with same url seems to empty entire history stack!)
    const currentRoute = this.getRouteFromUrl();
    if (currentRoute === newRoute) {
      LOG.info(`bookmark of newRoute skipped as same as current route: '${newRoute}'`);
      return;
    }
    this.rawBookmark(newRoute);
  }
  rawBookmark(newRoute, newUrl = "#" + newRoute) {
    LOG.debug(`**bookmark: BEFORE window.history.state: '${JSON.stringify(window.history.state, null, 2)}'`);
    // window.history.state allowed to be null on first entry into app (before app's first bookmark or replace)
    // Happens on a new browser tab, or when navigating to app from external site (e.g., google.com)
    if (this.currentRouteState !== AbSDKRouter.UNINITIALIZED_ROUTE_STATE && !isAbSDKRouteState(window.history.state)) {
      // Throws within abinitio, just logs as error in production.
      // In the past, happened when non-router code called history.{bookmark|replace} with an
      // invalid (non-AbSDKRouteState) state, so the AbSDKRouter state was incorrect.
      DebugUtils.developerError(`window.history.state is NOT an AbSDKRouteState for a bookmark call.  Current route: '${window.location.hash}', new route: '${newRoute}'`);
      // In production, try to recover by setting current state to what the router thinks is current AbSDKRouteState.
      // Note that the current route title may/may not match the currentRouteState, but not much we can do.
      LOG.warn(`Bookmark recovery strategy: reset current state to: ${JSON.stringify(this.currentRouteState, null, 2)}`);
      // Recovery strategy is 'restore CURRENT route state before bookmarking', so don't pass in newUrl to below!
      this.replaceWithRouteState(this.currentRouteState, true);
    }
    // adds <currentTitle, currentRoute> to history stack
    const newRouteState = buildAbSDKRouteState(newRoute);
    // side effect of pushState: puts current route(state) and routeTitle in Back history.
    // capture current window.history.state as 'prevEvState' as it will be state associated with Back history entry after pushState call.
    // Note window.history.state can be uninitialized (e.g., new browse tab)
    const prevEvState = window.history.state?.__routeState ?? undefined;
    LOG.debug(`**bookmark: push window.history.state: route=${prevEvState?.route}, timestamp=${prevEvState?.timestamp}, title=${document.title}`);
    window.history.pushState(newRouteState, '', newUrl);
    this.currentRouteState = newRouteState;
    LOG.info(`bookmark of newRoute '${newRoute}' pushed previous route on BACK history: '${prevEvState?.route}'`);
    const newEvState = window.history.state?.__routeState;
    // title will NOT change as newRoute title (aka document.title) updated AFTER bookmark call
    LOG.debug(`**bookmark: AFTER window.history.state: route=${newEvState?.route}, timestamp=${newEvState?.timestamp}`);
  }

  /** @inheritDoc */
  replace(route) {
    this.destroyGuard();
    route = this.canonicalRoute(route);
    if (route !== this.currentRouteState.__routeState.route) {
      // Update url with new route WITHOUT adding current route to history.
      const newRouteState = buildAbSDKRouteState(route);
      this.replaceWithRouteState(newRouteState);
    } else {
      LOG.info(`replace of newRoute skipped as same as current route: '${route}'`);
    }
  }
  replaceWithRouteState(newRouteState, forceReplace = false, newUrl = "#" + newRouteState.__routeState.route) {
    // Do NOT need to check if current window.history.state is valid, as just replacing it (so who cares!)
    const currRoute = this.currentRouteState.__routeState.route;
    if (newRouteState.__routeState.route !== currRoute || forceReplace) {
      window.history.replaceState(newRouteState, '', newUrl);
      this.currentRouteState = newRouteState;
      LOG.debug(`**replace AFTER currentRouteState: route=${newRouteState.__routeState.route}, timestamp=${newRouteState.__routeState.timestamp}`);
    } else {
      LOG.info(`replaceWithRouteState of newRoute skipped as same as current route: '${currRoute}'`);
    }
  }

  /** @inheritDoc */
  updateUrl(queryString, route, bookmarkCurrentUrl) {
    route = this.canonicalRoute(route);
    const canonicalQS = queryString.startsWith("?") ? queryString.substring(1) : queryString;
    // Only emit qs or route fragment if they're non-empty, which matches window.location.{search,hash} behavior
    const routeFragment = route ? "#" + route : "";
    const qsFragment = canonicalQS ? "?" + canonicalQS : "";
    const newUrl = `${window.location.origin}${window.location.pathname}${qsFragment}${routeFragment}`;
    if (newUrl === window.location.href) {
      LOG.info(`updateUrl skipped as same as current url: '${newUrl}'`);
      return;
    }
    // State only concerned with route part of url, but browser url will be updated to url value
    if (bookmarkCurrentUrl) {
      this.rawBookmark(route, newUrl);
    } else {
      const newRouteState = buildAbSDKRouteState(route);
      this.replaceWithRouteState(newRouteState, true, newUrl);
    }
  }

  //////////  Bookkeeping and Utilities  //////////////

  continueUrlChangeHandler(matchedRoute, historyInitiatedFn, afterRevertP) {
    const newRoute = this.buildBrowserRoute(matchedRoute);
    const continueRouteChangeContext = {
      newRoute,
      afterRevertP,
      historyInitiatedFn
    };
    if (!this.continueCallbacks.callUtilFirstFalse(continueRouteChangeContext)) {
      LOG.debug(`continueUrlChangeHandler listener is canceling navigation to route '${matchedRoute.route}'`);
      return false;
    }
    return true;
  }
  buildBrowserRoute(matchedRoute) {
    const {
      route,
      routeConfig,
      paramValues,
      extraParams
    } = matchedRoute;
    const paramMap = buildParamMap(routeConfig.paramNames, paramValues);
    return new BrowserRoute(routeConfig.routeTemplate, paramMap, extraParams, route);
  }
  parseExtraParams(routeWithExtraParams) {
    const extraParamIndex = routeWithExtraParams.indexOf("?");
    if (extraParamIndex < 0) {
      return {
        route: routeWithExtraParams,
        extraParams: {}
      };
    }
    const route = routeWithExtraParams.substring(0, extraParamIndex);
    const extraParamsStr = routeWithExtraParams.substring(extraParamIndex + 1);
    const extraParams = URLUtils.queryStringToObject(extraParamsStr);
    return {
      route,
      extraParams
    };
  }
  registerContinueRouteChangeListener(listener) {
    return this.continueCallbacks.addCallback(listener);
  }
  getUnknownRoute(route) {
    const missingRoute = this.buildUnknownRouteUrlFn(route);
    const matchRoute = this.getMatchedRoute(missingRoute);
    if (!matchRoute) {
      // Really in trouble if the Unknown Route is not found!
      throw new Error(`No route found for 'UNKNOWN_ROUTE': '${missingRoute}'`);
    }
    return matchRoute;
  }

  /** @inheritDoc */
  setBuildUnknownRouteUrlFn(buildUnknownRouteUrlFn) {
    this.removeRoute(AbSDKRouter.DEFAULT_UNKNOWN_ROUTE_TEMPLATE); // no-op if already been removed
    this.buildUnknownRouteUrlFn = buildUnknownRouteUrlFn;
  }
  addDefaultUnknownRoute() {
    // In practice, this default is only used for abweb test-webapp,
    // as NavigationService always registers their own 'UnknownRouteRoute' via  setBuildUnknownRouteUrlFn
    this.addRoute(AbSDKRouter.DEFAULT_UNKNOWN_ROUTE_TEMPLATE,
    // RouteCallback: No known target to render html into, so simply throw an alert.
    (route, extraParams) => {
      // Don't want invalid 'route' in browser url, so fix it
      // routeCallback responsible for updating route url and title
      const routeUrl = calcRouteWithParameters(AbSDKRouter.DEFAULT_UNKNOWN_ROUTE_TEMPLATE, {
        route
      });
      this.replace(routeUrl);
      alert(`No route found for route: '${route}'`);
    });
    return route => calcRouteWithParameters(AbSDKRouter.DEFAULT_UNKNOWN_ROUTE_TEMPLATE, {
      route
    });
  }
  destroy() {
    if (!this.isDestroyed) {
      this.routeConfigs.length = 0;
      this.continueCallbacks.removeAllCallbacks();
      window.removeEventListener('popstate', this.onPopStateListener);
      this.isDestroyed = true;
    }
  }
  destroyGuard() {
    if (this.isDestroyed) {
      throw new Error("Can not use router after it has been destroyed!");
    }
  }
}
_defineProperty(AbSDKRouter, "DEFAULT_UNKNOWN_ROUTE_TEMPLATE", "/error/routeUnknownDefault/:route");
_defineProperty(AbSDKRouter, "UNINITIALIZED_ROUTE_STATE", buildAbSDKRouteState("__not_initialized_yet__"));
//# sourceMappingURL=AbSDKRouter.js.map
